import React, {useRef, useState} from "react";
import PropTypes from "prop-types";
import {useSelector} from "react-redux";
import {useTheme} from "connectui/theme/ThemeProvider";
import FlexContainer from "connectui/component/layout/FlexContainer";
import Container from "connectui/component/layout/Container";
import Text from "connectui/component/display/Text";
import ButtonSegmentedGroup from "connectui/component/input/button/ButtonSegmentedGroup";
import ButtonText from "connectui/component/input/button/ButtonText";
import ButtonIconText from "connectui/component/input/button/ButtonIconText";
import DropdownSelect from "connectui/component/input/dropdown/DropDownSelect";
import OutlinedDropDownBase from "connectui/component/input/dropdown/base/OutlinedDropDownBase";
import OutlinedButtonBase from "connectui/component/input/button/base/OutlinedButtonBase";
import ModalPortal from "connectui/component/modal/ModalPortal";
import Calendar from "connectui/component/input/calendar/Calendar";
import SvgIcon from "connectui/component/display/SvgIcon";
import ICONS from "connectui/icons/iconMap";
import {endOfDay, startOfDay, subDays} from "date-fns";
import ListingGroupSelectorMenu from "../shared/context/ListingGroupSelectorMenu";
import {useTranslation} from "react-i18next";

const DashboardActionBar = ({
  onTimeframeChange,
  selectedListingGroups = [],
  onListingGroupsChange,
  selectedCurrency = "USD",
  onCurrencyChange,
}) => {
  const {theme, isMobile} = useTheme();
  const revenueData = useSelector(
    (state) => state.dashboardReducer.revenueData,
  );

  const {t} = useTranslation();
  const [selectedTimeRange, setSelectedTimeRange] = useState(3);
  const [customTimeOpen, setCustomTimeOpen] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [groupsMenuOpen, setGroupsMenuOpen] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [groupsAnchorEl, setGroupsAnchorEl] = useState(null);
  const buttonGroupRef = useRef(null);

  const availableCurrencies = revenueData?.available_currencies || [];
  const displayCurrency =
    selectedCurrency || revenueData?.display_currency || "USD";
  const currenciesLoaded = revenueData && revenueData.available_currencies;

  const handleTimeRangeChange = (index) => {
    setSelectedTimeRange(index);

    if (index === 4 && !isMobile) {
      setCustomTimeOpen(true);
      return;
    }

    const now = new Date();
    let startDate, endDate;

    switch (index) {
      case 0:
        startDate = new Date(startOfDay(subDays(now, 1)));
        endDate = new Date(endOfDay(now));
        break;
      case 1:
        startDate = new Date(startOfDay(subDays(now, 6)));
        endDate = new Date(endOfDay(now));
        break;
      case 2:
        startDate = new Date(startOfDay(subDays(now, 13)));
        endDate = new Date(endOfDay(now));
        break;
      case 3:
        startDate = new Date(startOfDay(subDays(now, 29)));
        endDate = new Date(endOfDay(now));
        break;
      default:
        startDate = new Date(startOfDay(subDays(now, 29)));
        endDate = new Date(endOfDay(now));
    }

    const timeframe = {
      startDate: startDate.getTime(),
      endDate: endDate.getTime(),
    };

    onTimeframeChange(timeframe);
  };

  const handleCustomDateChange = (date) => {
    if (Array.isArray(date) && date.length === 2) {
      setSelectedDates(date);
      const timeframe = {
        startDate: date[0].getTime(),
        endDate: date[1].getTime(),
      };
      onTimeframeChange(timeframe);
      setCustomTimeOpen(false);
      return;
    }

    const [startDate, endDate] = selectedDates;

    if (!endDate && !!startDate) {
      const newRange = [startDate, new Date(date.setHours(23, 59, 59, 999))];
      setSelectedDates(newRange);
      const timeframe = {
        startDate: newRange[0].getTime(),
        endDate: newRange[1].getTime(),
      };
      onTimeframeChange(timeframe);
      setCustomTimeOpen(false);
    } else {
      setSelectedDates([new Date(date.setHours(0, 0, 0, 0)), null]);
    }
  };

  const handleCustomTimeClose = () => {
    setCustomTimeOpen(false);
  };

  const handleGroupsMenuOpen = (e) => {
    setGroupsAnchorEl(e.currentTarget);
    setGroupsMenuOpen(true);
  };

  const handleGroupsMenuClose = () => {
    setGroupsAnchorEl(null);
    setGroupsMenuOpen(false);
  };

  const handleGroupSelection = (groupId, isSelected) => {
    let newSelection;
    if (isSelected) {
      newSelection = selectedGroupIds.filter((id) => id !== groupId);
    } else {
      newSelection = [...selectedGroupIds, groupId];
    }
    setSelectedGroupIds(newSelection);
    onListingGroupsChange(newSelection);
  };

  const getButtonLabel = () => {
    if (selectedGroupIds.length === 0) {
      return t("dashboard.header.listing-groups-all");
    } else if (selectedGroupIds.length === 1) {
      return t("dashboard.header.listing-groups-one");
    } else {
      return (
        `${selectedGroupIds.length} ` +
        t("dashboard.header.listing-groups-suffix")
      );
    }
  };

  const renderCurrencySelector = () => {
    if (!currenciesLoaded) {
      return (
        <Container
          skeleton={{
            enabled: true,
            width: "80px",
            height: "44px",
          }}
          sx={{
            minWidth: "80px",
          }}
        />
      );
    }

    return (
      <DropdownSelect
        dropDownBase={OutlinedDropDownBase}
        customLabel={
          <Text variant="textMD" weight={"regular"}>
            {displayCurrency}
          </Text>
        }
        selected={displayCurrency}
        onChange={onCurrencyChange}
        sx={{
          minWidth: "80px",
          label: {
            padding: theme.spacing(2, 3),
          },
        }}
      >
        {availableCurrencies.map((currency) => (
          <FlexContainer key={currency} value={currency}>
            <Text variant="textMD" weight="regular">
              {currency}
            </Text>
          </FlexContainer>
        ))}
      </DropdownSelect>
    );
  };

  return (
    <FlexContainer
      alignItems="center"
      justifyContent="space-between"
      sx={{
        marginBottom: theme.spacing(4),
        width: "100%",
        flexWrap: "wrap",
        gap: theme.spacing(2),
      }}
    >
      <FlexContainer alignItems="center" gap={theme.spacing(2)}>
        <ButtonText
          buttonBase={OutlinedButtonBase}
          label={getButtonLabel()}
          typographyVariant={"textMD"}
          weight={"regular"}
          onClick={handleGroupsMenuOpen}
          sx={{
            minWidth: "180px",
            maxWidth: "200px",
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
          }}
        />

        {renderCurrencySelector()}

        <ListingGroupSelectorMenu
          open={groupsMenuOpen}
          onClose={handleGroupsMenuClose}
          anchorEl={groupsAnchorEl}
          selectedIds={selectedGroupIds}
          onGroupSelection={handleGroupSelection}
        />
      </FlexContainer>

      <FlexContainer alignItems="center" gap={theme.spacing(2)}>
        <Container ref={buttonGroupRef}>
          <ButtonSegmentedGroup
            selectedIndex={selectedTimeRange}
            onChange={handleTimeRangeChange}
          >
            <ButtonText label="24 hours" as="div" />
            <ButtonText label="7 days" as="div" />
            <ButtonText label="14 days" as="div" />
            <ButtonText label="30 days" as="div" />
            {!isMobile && (
              <ButtonIconText
                buttonBase={OutlinedButtonBase}
                label={t("dashboard.header.custom-time")}
                as="div"
                icon={
                  <SvgIcon
                    component={ICONS.calendar}
                    sx={{
                      width: "16px",
                      height: "16px",
                      marginRight: theme.spacing(1),
                    }}
                  />
                }
              />
            )}
          </ButtonSegmentedGroup>
        </Container>

        {customTimeOpen && !isMobile && (
          <ModalPortal
            isOpen={customTimeOpen}
            onClose={handleCustomTimeClose}
            anchorEl={buttonGroupRef.current}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "end",
            }}
            modalPosition={{
              vertical: "top",
              horizontal: "end",
            }}
          >
            <Container
              sx={{
                width: "290px",
              }}
            >
              <Calendar
                selectedRange={selectedDates}
                onDateClick={handleCustomDateChange}
              />
            </Container>
          </ModalPortal>
        )}
      </FlexContainer>
    </FlexContainer>
  );
};

DashboardActionBar.propTypes = {
  onTimeframeChange: PropTypes.func.isRequired,
  selectedListingGroups: PropTypes.array,
  onListingGroupsChange: PropTypes.func,
  selectedCurrency: PropTypes.string,
  onCurrencyChange: PropTypes.func,
};

export default DashboardActionBar;
